import React from 'react';

/**
 * Renders the SEO intro banner at the top of a category search page.
 * Driven entirely by a seoConfig data object — no category-specific logic here.
 *
 * @param {Object} props
 * @param {boolean} props.collapsed - Whether to show the compact single-line version
 * @param {Object} props.seoConfig - Category SEO config from seo/index.js
 */
const SeoIntro = ({ collapsed, seoConfig }) => {
  const { accentColor, links, intro } = seoConfig;

  // Build the gradient using the config's accent color
  const accentGradient = `linear-gradient(90deg, var(${accentColor}), var(${
    accentColor === '--colorArt' ? '--colorJudaica' : '--colorArt'
  }), var(${accentColor}))`;

  if (collapsed) {
    return (
      <div className="pt-3 pb-1">
        <div
          className="relative overflow-hidden rounded-xl border border-stone-200/60 px-5 py-4 md:px-8 md:py-4 flex items-center justify-between gap-4 flex-wrap"
          style={{
            background: 'linear-gradient(135deg, #f8f6f1 0%, #f1efe8 40%, #eef0ea 100%)',
          }}
        >
          <div
            className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: accentGradient }}
          />
          <h2 className="text-base md:text-lg font-bold text-stone-900 font-['Be_Vietnam_Pro'] leading-tight">
            {intro.collapsedHeading}
          </h2>
          <div className="flex flex-wrap gap-1.5">
            {links.map(link => (
              <a
                key={link.key}
                href={link.href}
                className={`px-3 py-1 text-xs font-medium text-stone-500 bg-white/70 rounded-full border border-stone-200 hover:border-[var(${accentColor})] hover:text-[var(${accentColor})] transition-all duration-200`}
              >
                {link.title}
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-1.5 pb-1">
      <div
        className="relative overflow-hidden rounded-xl border border-stone-200/60 px-3 py-1.5 md:px-4 md:py-2"
        style={{
          background: 'linear-gradient(135deg, #f8f6f1 0%, #f1efe8 40%, #eef0ea 100%)',
        }}
      >
        {/* Subtle decorative accent line */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: accentGradient }}
        />

        {/* Kicker */}
        {intro.kicker && (
          <p
            className={`text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(${accentColor})] mt-0.5 mb-0`}
          >
            {intro.kicker}
          </p>
        )}

        {/* Title */}
        <h1 className="text-lg md:text-2xl font-bold text-stone-400 font-['Be_Vietnam_Pro'] mb-0 leading-[1.15]">
          {intro.heading}
        </h1>

        {/* Marketplace tagline */}
        <p className="text-xs md:text-sm text-stone-500 mb-0 leading-snug">
          {intro.tagline}
        </p>

        {/* SEO body copy */}
        <div className="space-y-0.5 mb-1">
          {intro.bodyParagraphs.map((html, i) => (
            <p
              key={i}
              className="text-[12px] md:text-[13px] text-stone-500 leading-snug mb-0"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ))}
        </div>

        {/* Category / material links */}
        <div>
          {intro.linksLabel && (
            <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400 mt-0.5 mb-1">
              {intro.linksLabel}
            </h3>
          )}
          <div className="flex flex-wrap gap-1.5">
            {links.map(link => (
              <a
                key={link.key}
                href={link.href}
                className={`px-3 py-1 text-[12px] font-medium rounded-full border transition-all duration-200 text-stone-600 bg-white/70 backdrop-blur-sm border-stone-200 hover:border-[var(${accentColor})] hover:text-[var(${accentColor})] hover:bg-white`}
              >
                {link.title}
              </a>
            ))}
          </div>
          {intro.linksNote && (
            <p
              className="text-[11px] text-stone-400 mt-1.5 mb-0 leading-snug"
              dangerouslySetInnerHTML={{ __html: intro.linksNote }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SeoIntro;
